import { useEffect, useState, useCallback } from "react";
import { API } from "@/api";
import { getAnnouncementRead } from "@/api/services/content.service";
import { X, ExternalLink } from "lucide-react";

interface Announcement {
  id: number;
  title: string;
  body: string;
  image_url?: string;
  link?: string;
  android_link?: string;
  ios_link?: string;
}

interface AnnouncementBottomSheetProps {
  onComplete?: () => void;
}

const getPlatformLink = (announcement: Announcement): string | undefined => {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);

  if (isIOS && announcement.ios_link) return announcement.ios_link;
  if (isAndroid && announcement.android_link) return announcement.android_link;
  return announcement.link;
};

export const AnnouncementBottomSheet = ({ onComplete }: AnnouncementBottomSheetProps) => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isDetailExpanded, setIsDetailExpanded] = useState(false);

  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const res = await API.get(`/api/v2/notices/announce/`);
        const data: Announcement[] = res.data.results || res.data || [];
        if (data.length === 0) return;

        // Check each announcement for read status via API
        const unreadAnnouncements: Announcement[] = [];
        for (const announcement of data) {
          try {
            const readResponse = await getAnnouncementRead(String(announcement.id));
            const readData = readResponse.data;
            // If no read record exists, add to unread list
            if (!readData || readData.length === 0) {
              unreadAnnouncements.push(announcement);
            }
          } catch (error) {
            // If error checking read status, include the announcement
            unreadAnnouncements.push(announcement);
          }
        }

        if (unreadAnnouncements.length > 0) {
          setAnnouncements(unreadAnnouncements);
          requestAnimationFrame(() => {
            setIsVisible(true);
            requestAnimationFrame(() => setIsAnimating(true));
          });
        }
      } catch (error) {
        console.error("Failed to fetch announcements:", error);
      }
    };

    fetchAnnouncements();
  }, []);

  useEffect(() => {
    setIsDetailExpanded(false);
  }, [currentIndex]);

  const closeWithAnimation = useCallback(() => {
    setIsAnimating(false);
    setTimeout(() => {
      setIsVisible(false);
      onComplete?.();
    }, 300);
  }, [onComplete]);

  const handleClose = useCallback(() => {
    if (announcements.length === 0) return;
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      closeWithAnimation();
    }
  }, [announcements, currentIndex, closeWithAnimation]);

  const handleDontShowToday = useCallback(() => {
    if (announcements.length === 0) return;
    // Mark only the current announcement as read via API
    const currentAnnouncement = announcements[currentIndex];
    if (currentAnnouncement) {
      API.post("/api/v2/notices/announce-read/", {
        announcement_id: currentAnnouncement.id,
      }).catch((error) => {
        console.error("Failed to mark announcement as read:", error);
      });
    }
    if (currentIndex < announcements.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      closeWithAnimation();
    }
  }, [announcements, currentIndex, closeWithAnimation]);

  const handleLinkClick = (link: string) => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  if (!isVisible || announcements.length === 0) return null;

  const currentAnnouncement = announcements[currentIndex];
  const platformLink = getPlatformLink(currentAnnouncement);
  const titleText = (currentAnnouncement.title || "").trim();
  const bodyText = (currentAnnouncement.body || "").trim();
  const hasDetailContent = Boolean(titleText || bodyText);
  const detailSectionId = `announcement-detail-${currentAnnouncement.id}`;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-end justify-center transition-opacity duration-300 ${
        isAnimating ? "opacity-100" : "opacity-0"
      }`}
    >
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleClose}
        aria-hidden="true"
      />

      <div
        className={`relative bg-white w-full max-w-[640px] rounded-t-3xl overflow-hidden transition-transform duration-300 ease-out ${
          isAnimating ? "translate-y-0" : "translate-y-full"
        }`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleText ? "announcement-title" : undefined}
        aria-label={titleText ? undefined : "공지사항"}
      >
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 bg-black/30 hover:bg-black/50 rounded-full flex items-center justify-center transition-colors"
          aria-label="닫기"
        >
          <X size={16} className="text-white" />
        </button>

        {currentAnnouncement.image_url && (
          <div className="relative w-full aspect-square bg-gray-100 overflow-hidden">
            <img
              src={currentAnnouncement.image_url}
              alt={currentAnnouncement.title}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        )}

        {hasDetailContent && (
          <div
            id={detailSectionId}
            className={`px-6 overflow-hidden transition-all duration-300 ${
              isDetailExpanded ? "max-h-80 pt-5 pb-4 opacity-100" : "max-h-0 pt-0 pb-0 opacity-0"
            }`}
          >
            {titleText && (
              <h3
                id="announcement-title"
                className="text-xl font-bold text-black"
                style={{ fontFamily: "Gmarket-B" }}
              >
                {titleText}
              </h3>
            )}
            {bodyText && (
              <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                {bodyText}
              </p>
            )}
          </div>
        )}

        <div className="px-6 pb-6 pt-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {hasDetailContent && (
              <button
                type="button"
                onClick={() => setIsDetailExpanded((prev) => !prev)}
                className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
                aria-expanded={isDetailExpanded}
                aria-controls={detailSectionId}
              >
                {isDetailExpanded ? "접기" : "자세히 보기"}
              </button>
            )}
            {platformLink && (
              <button
                type="button"
                onClick={() => handleLinkClick(platformLink)}
                className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 transition-colors"
              >
                링크 열기
                <ExternalLink size={12} />
              </button>
            )}
          </div>
          <button
            type="button"
            onClick={handleDontShowToday}
            className="text-sm text-gray-600 hover:text-gray-800 transition-colors"
          >
            오늘 하루 보지 않기
          </button>

          {announcements.length > 1 && (
            <div className="flex gap-1.5">
              {announcements.map((_, idx) => (
                <div
                  key={idx}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentIndex ? "bg-black" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="h-safe-area-inset-bottom" />
      </div>
    </div>
  );
};

export default AnnouncementBottomSheet;
